import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Sparkles } from 'lucide-react';
import { SemanticHit, timeAgo, isStale } from './types';

// Semantic (vector) search results — Confluence and Drive tabs only
// (open decision #1: per-tab mode matching, recommended option).
//
// Results are ranked by relevance against chunk embeddings rather than
// filtered by keyword, so each hit shows a relevance score and the matched
// content excerpt instead of table columns.

interface Props {
  hits: SemanticHit[];
  accentColor: string;
  query: string;
  onSelect: (hit: SemanticHit) => void;
}
export function SemanticResults({
  hits,
  accentColor,
  query,
  onSelect
}: Props) {
  return <div data-id="element-773">
      <div className="flex items-center gap-2 mb-4" data-id="element-774">
        <Sparkles size={14} className="text-[#4a9a9a]" data-id="element-775" />
        <span className="text-[13px] text-[#8a8680]" data-id="element-776">
          {hits.length} results for “{query}” · ranked by relevance · semantic search over indexed content
        </span>
      </div>
      <div className="flex flex-col gap-3" data-id="element-777">
        {hits.map((hit, i) => <motion.button key={hit.record.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        delay: i * 0.05
      }} onClick={() => onSelect(hit)} className="bg-[#1c1c22] rounded-[16px] p-5 shadow-xl shadow-black/20 text-left w-full hover:bg-[#1e1e25] transition-colors group" data-id="element-778">
            <div className="flex items-center justify-between gap-4" data-id="element-779">
              <div className="flex items-center gap-3 min-w-0" data-id="element-780">
                <span className="text-[14px] text-[#e8e4df] font-medium truncate" data-id="element-781">
                  {hit.record.title}
                </span>
                {hit.record.cells.space && <span className="text-[12px] px-2 py-0.5 rounded-full bg-[#252530] text-[#8a8680] whitespace-nowrap shrink-0" data-id="element-782">
                    {hit.record.cells.space}
                  </span>}
                {hit.record.cells.type && <span className="text-[12px] px-2 py-0.5 rounded-full bg-[#252530] text-[#8a8680] whitespace-nowrap shrink-0" data-id="element-783">
                    {hit.record.cells.type}
                  </span>}
              </div>
              <div className="flex items-center gap-3 shrink-0" data-id="element-784">
                {/* Relevance score bar */}
                <div className="flex items-center gap-2" data-id="element-785">
                  <div className="w-16 h-1.5 rounded-full bg-[#252530] overflow-hidden" data-id="element-786">
                    <div className="h-full rounded-full" style={{
                  width: `${hit.relevance * 100}%`,
                  background: accentColor
                }} data-id="element-787" />
                  </div>
                  <span className="text-[12px] text-[#8a8680] w-8" data-id="element-788">{Math.round(hit.relevance * 100)}%</span>
                </div>
                {hit.record.externalUrl && <span className="text-[#8a8680]/40 group-hover:text-[#4a9a9a] transition-colors" data-id="element-789">
                    <ExternalLink size={14} data-id="element-790" />
                  </span>}
              </div>
            </div>
            {/* Matched chunk excerpt */}
            <p className="text-[13px] text-[#8a8680] leading-relaxed mt-2 line-clamp-2" data-id="element-791">{hit.excerpt}</p>
            <div className="flex items-center gap-3 mt-2" data-id="element-792">
              <span className="text-[12px] text-[#8a8680]/40" data-id="element-793">{hit.record.cells.date}</span>
              <span className={`text-[12px] ${isStale(hit.record.syncedAt) ? 'text-[#e8a84c]' : 'text-[#8a8680]/40'}`} data-id="element-794">
                synced {timeAgo(hit.record.syncedAt)}
                {isStale(hit.record.syncedAt) && ' ⚠'}
              </span>
            </div>
          </motion.button>)}
      </div>
    </div>;
}